import React, { useMemo } from 'react'
import { ResizablePanels } from '@structura/ui'
import { useResizablePanels } from '@structura/ui'
import { CrossSection, SectionMaterialStrength, SMBeamModel } from '../types'
import { LoadFormState, LoadState, LoadTypeOption } from '../hooks/useLoadState'
import { ImposedLoadCategory } from '../hooks/useMaterialState'
import LoadConfigurationPanel from './loads/LoadConfigurationPanel'
import LoadVisualizationPanel from './loads/LoadVisualizationPanel'

interface LoadsTabProps
{
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  selectedSpan: number
  setSelectedSpan: (spanId: number) => void
  selectedLoadType: LoadTypeOption
  setSelectedLoadType: (type: LoadTypeOption) => void
  loadForm: LoadFormState
  setLoadForm: LoadState['setLoadForm']
  addLoadToSpan: () => void
  removeLoad: (loadId: string, spanId: number) => void
  editLoad: (loadId: string, spanId: number) => void
  editingLoadId: string | null
  setEditingLoadId: (id: string | null) => void
  calculateSelfWeight: (crossSection: CrossSection) => number
  currentMaterial: string
  timberKind: 'glulam' | 'solid'
  sectionMaterialStrength: SectionMaterialStrength | null | undefined
  // Material details for PT cable loss calculations
  materialDetails?: any
  concreteRelativeHumidity?: number
  concreteNotionalSize?: number
  concreteLoadingAge?: number
  concreteCementType?: 'S' | 'N' | 'R'
  reliabilityClass: 1 | 2 | 3
  setReliabilityClass: React.Dispatch<React.SetStateAction<1 | 2 | 3>>
  applyRc1Reduction: boolean
  setApplyRc1Reduction: React.Dispatch<React.SetStateAction<boolean>>
  imposedLoadCategory: ImposedLoadCategory
  setImposedLoadCategory: React.Dispatch<React.SetStateAction<ImposedLoadCategory>>
}

const LoadsTab: React.FC<LoadsTabProps> = ({
  model,
  setModel,
  selectedSpan,
  setSelectedSpan,
  selectedLoadType,
  setSelectedLoadType,
  loadForm,
  setLoadForm,
  addLoadToSpan,
  removeLoad,
  editLoad,
  editingLoadId,
  setEditingLoadId,
  calculateSelfWeight,
  currentMaterial,
  timberKind,
  sectionMaterialStrength,
  materialDetails,
  concreteRelativeHumidity = 50,
  concreteNotionalSize = 200,
  concreteLoadingAge = 28,
  concreteCementType = 'N',
  reliabilityClass,
  setReliabilityClass,
  applyRc1Reduction,
  setApplyRc1Reduction,
  imposedLoadCategory,
  setImposedLoadCategory
}) =>
{
  const { leftWidth, rightWidth, handleMouseDown, isDragging, containerRef } = useResizablePanels({
    defaultLeftWidth: 30,
    minLeftWidth: 3,
    maxLeftWidth: 97,
    storageKey: 'beamDesigner_loadsTab_leftWidth'
  })

  // Build material props for PT cable loss calculations
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- optional chaining deps are intentional
  const prestressingMaterial = useMemo(() => {
    if (!materialDetails?.prestressing) return undefined
    const p = materialDetails.prestressing
    return {
      fpk: p.fpk,
      fp01k: p.fp01k,
      elasticModulus: p.elasticModulus,
      relaxationClass: p.relaxationClass ?? 2,
      gammaS: p.gammaS ?? 1.15
    }
  }, [materialDetails?.prestressing])

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- optional chaining deps are intentional
  const concreteMaterial = useMemo(() => {
    if (!materialDetails?.fck || !materialDetails?.elasticModulus) return undefined
    return {
      fck: materialDetails.fck,
      elasticModulus: materialDetails.elasticModulus,
      relativeHumidity: concreteRelativeHumidity,
      notionalSize: concreteNotionalSize,
      loadingAge: concreteLoadingAge,
      cementType: concreteCementType
    }
  }, [materialDetails?.fck, materialDetails?.elasticModulus, concreteRelativeHumidity, concreteNotionalSize, concreteLoadingAge, concreteCementType])

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- optional chaining deps are intentional
  const crossSectionProps = useMemo(() => {
    if (!model.crossSection?.area || !model.crossSection?.inertia) return undefined
    return {
      area: model.crossSection.area,
      inertia: model.crossSection.inertia,
      heightMm: model.crossSection.height // Height in mm for PT eccentricity calculation
    }
  }, [model.crossSection?.area, model.crossSection?.inertia, model.crossSection?.height])

  return (
    <ResizablePanels
      leftPanel={(
        <LoadConfigurationPanel
          model={model}
          setModel={setModel}
          selectedSpan={selectedSpan}
          setSelectedSpan={setSelectedSpan}
          selectedLoadType={selectedLoadType}
          setSelectedLoadType={setSelectedLoadType}
          loadForm={loadForm}
          setLoadForm={setLoadForm}
          addLoadToSpan={addLoadToSpan}
          removeLoad={removeLoad}
          editLoad={editLoad}
          editingLoadId={editingLoadId}
          setEditingLoadId={setEditingLoadId}
          calculateSelfWeight={calculateSelfWeight}
          currentMaterial={currentMaterial}
          prestressingMaterial={prestressingMaterial}
          concreteMaterial={concreteMaterial}
          crossSection={crossSectionProps}
          reliabilityClass={reliabilityClass}
          setReliabilityClass={setReliabilityClass}
          applyRc1Reduction={applyRc1Reduction}
          setApplyRc1Reduction={setApplyRc1Reduction}
          imposedLoadCategory={imposedLoadCategory}
          setImposedLoadCategory={setImposedLoadCategory}
        />
      )}
      rightPanel={(
        <LoadVisualizationPanel
          model={model}
          currentMaterial={currentMaterial}
          timberKind={timberKind}
          sectionMaterialStrength={sectionMaterialStrength}
        />
      )}
      leftWidth={leftWidth}
      rightWidth={rightWidth}
      onMouseDown={handleMouseDown}
      isDragging={isDragging}
      containerRef={containerRef}
    />
  )
}

export default LoadsTab
